import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Logo } from '../components/Logo';
import { 
  Package, 
  Users, 
  DollarSign,
  TrendingUp,
  ShoppingBag,
  Clock,
  CheckCircle,
  Truck,
  XCircle,
  Search,
  Filter,
  ChevronRight,
  RefreshCw,
  Loader2,
  LayoutDashboard,
  Settings,
  LogOut
} from 'lucide-react';

const AdminDashboard = () => {
  const { api, user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, ordersRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/orders')
      ]);
      setStats(statsRes.data);
      setOrders(ordersRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-400 bg-yellow-400/10';
      case 'processing': return 'text-blue-400 bg-blue-400/10';
      case 'shipped': return 'text-purple-400 bg-purple-400/10';
      case 'delivered': return 'text-green-400 bg-green-400/10';
      case 'cancelled': return 'text-red-400 bg-red-400/10';
      default: return 'text-zinc-400 bg-zinc-400/10';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'processing': return <Package className="w-4 h-4" />;
      case 'shipped': return <Truck className="w-4 h-4" />;
      case 'delivered': return <CheckCircle className="w-4 h-4" />;
      case 'cancelled': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.shipping_address?.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.shipping_address?.last_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#090D0F] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#090D0F] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[#0A0D0F] border-r border-zinc-800 flex flex-col">
        <div className="p-6 border-b border-zinc-800">
          <Logo linkTo="/admin" size="default" />
          <span className="text-xs text-teal-400 font-medium mt-2 block">ADMIN PORTAL</span>
        </div>
        
        <nav className="flex-1 p-4 space-y-2">
          <button
            onClick={() => setActiveTab('overview')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
              activeTab === 'overview' ? 'bg-teal-400/10 text-teal-400' : 'text-zinc-400 hover:bg-zinc-800'
            }`}
          >
            <LayoutDashboard className="w-5 h-5" />
            Overview
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
              activeTab === 'orders' ? 'bg-teal-400/10 text-teal-400' : 'text-zinc-400 hover:bg-zinc-800'
            }`}
          >
            <Package className="w-5 h-5" />
            Orders
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
              activeTab === 'users' ? 'bg-teal-400/10 text-teal-400' : 'text-zinc-400 hover:bg-zinc-800'
            }`}
          >
            <Users className="w-5 h-5" />
            Users
          </button>
        </nav>

        <div className="p-4 border-t border-zinc-800">
          <Link to="/dashboard">
            <Button variant="ghost" className="w-full justify-start text-zinc-400 hover:text-white">
              <ChevronRight className="w-4 h-4 mr-2 rotate-180" />
              Back to App
            </Button>
          </Link>
          <Button 
            variant="ghost" 
            onClick={logout}
            className="w-full justify-start text-zinc-400 hover:text-red-400 mt-2"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Header */}
        <header className="sticky top-0 z-10 bg-[#090D0F]/80 backdrop-blur border-b border-zinc-800 px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold">
              {activeTab === 'overview' && 'Dashboard Overview'}
              {activeTab === 'orders' && 'Order Management'}
              {activeTab === 'users' && 'User Management'}
            </h1>
            <Button onClick={fetchData} variant="outline" className="border-zinc-700">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </header>

        <div className="p-8">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-[#121416] border border-zinc-800 rounded-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-zinc-500 text-sm">Total Revenue</span>
                    <DollarSign className="w-5 h-5 text-teal-400" />
                  </div>
                  <p className="text-3xl font-bold">${stats?.total_revenue?.toLocaleString() || '0'}</p>
                  <p className="text-sm text-green-400 mt-2">+12% from last month</p>
                </div>

                <div className="bg-[#121416] border border-zinc-800 rounded-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-zinc-500 text-sm">Total Orders</span>
                    <ShoppingBag className="w-5 h-5 text-teal-400" />
                  </div>
                  <p className="text-3xl font-bold">{stats?.total_orders || orders.length}</p>
                  <p className="text-sm text-zinc-500 mt-2">{orders.filter(o => o.status === 'pending').length} pending</p>
                </div>

                <div className="bg-[#121416] border border-zinc-800 rounded-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-zinc-500 text-sm">Total Users</span>
                    <Users className="w-5 h-5 text-teal-400" />
                  </div>
                  <p className="text-3xl font-bold">{stats?.total_users || 0}</p>
                  <p className="text-sm text-zinc-500 mt-2">{stats?.active_subscriptions || 0} subscribers</p>
                </div>

                <div className="bg-[#121416] border border-zinc-800 rounded-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-zinc-500 text-sm">Active Classes</span>
                    <TrendingUp className="w-5 h-5 text-teal-400" />
                  </div>
                  <p className="text-3xl font-bold">{stats?.total_classes || 0}</p>
                  <p className="text-sm text-zinc-500 mt-2">{stats?.total_instructors || 0} instructors</p>
                </div>
              </div>

              {/* Recent Orders */}
              <div className="bg-[#121416] border border-zinc-800 rounded-sm">
                <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
                  <h2 className="text-lg font-bold">Recent Orders</h2>
                  <Button 
                    variant="ghost" 
                    onClick={() => setActiveTab('orders')}
                    className="text-teal-400"
                  >
                    View All
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
                <div className="divide-y divide-zinc-800">
                  {orders.slice(0, 5).map((order) => (
                    <div key={order.id} className="p-6 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-sm ${getStatusColor(order.status)}`}>
                          {getStatusIcon(order.status)}
                        </div>
                        <div>
                          <p className="font-medium">Order #{order.id.slice(0, 8).toUpperCase()}</p>
                          <p className="text-sm text-zinc-500">
                            {order.shipping_address?.first_name} {order.shipping_address?.last_name}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">${order.total?.toLocaleString()}</p>
                        <p className="text-sm text-zinc-500">{new Date(order.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  ))}
                  {orders.length === 0 && (
                    <div className="p-12 text-center text-zinc-500">
                      No orders yet
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Orders Tab */}
          {activeTab === 'orders' && (
            <div className="space-y-6">
              {/* Filters */}
              <div className="flex gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                  <Input
                    placeholder="Search orders..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-[#121416] border-zinc-700"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="bg-[#121416] border border-zinc-700 rounded-sm px-4 py-2 text-white"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              {/* Orders Table */}
              <div className="bg-[#121416] border border-zinc-800 rounded-sm overflow-hidden">
                <table className="w-full">
                  <thead className="bg-zinc-800/50">
                    <tr>
                      <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Order ID</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Customer</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Items</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Total</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Status</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Date</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800">
                    {filteredOrders.map((order) => (
                      <tr key={order.id} className="hover:bg-zinc-800/30 transition-colors">
                        <td className="px-6 py-4">
                          <span className="font-mono text-sm">#{order.id.slice(0, 8).toUpperCase()}</span>
                        </td>
                        <td className="px-6 py-4">
                          <p className="font-medium">{order.shipping_address?.first_name} {order.shipping_address?.last_name}</p>
                          <p className="text-sm text-zinc-500">{order.shipping_address?.city}, {order.shipping_address?.state}</p>
                        </td>
                        <td className="px-6 py-4">
                          {order.items?.map((item, i) => (
                            <p key={i} className="text-sm">{item.name} x{item.quantity}</p>
                          ))}
                        </td>
                        <td className="px-6 py-4 font-bold">${order.total?.toLocaleString()}</td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-medium ${getStatusColor(order.status)}`}>
                            {getStatusIcon(order.status)}
                            {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-zinc-400">
                          {new Date(order.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          <Link to={`/admin/orders/${order.id}`}>
                            <Button variant="ghost" size="sm" className="text-teal-400">
                              View
                            </Button>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredOrders.length === 0 && (
                  <div className="p-12 text-center text-zinc-500">
                    No orders found
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="bg-[#121416] border border-zinc-800 rounded-sm p-12 text-center">
              <Users className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">User Management</h3>
              <p className="text-zinc-500">User management features coming soon.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
